import { FC } from 'react';

import { Link } from '@inertiajs/react';
import { CircleAlert, CircleCheck, TriangleAlert } from 'lucide-react';

import { Alert, AlertTitle } from '../ui/Alert';

type FlashValue =
    | string
    | {
          message: string;
          linkUrl?: string;
          linkText?: string;
      };

export type ProfileFlash = {
    notice?: FlashValue;
    alert?: FlashValue;
    warning?: FlashValue;
};

type Props = {
    flash?: ProfileFlash;
};

const content = (value: FlashValue) => {
    if (typeof value === 'string') return value;

    return (
        <>
            {value.message}
            {value.linkUrl && (
                <>
                    {' '}
                    <Link
                        href={value.linkUrl}
                        className="font-medium underline"
                    >
                        {value.linkText || value.linkUrl}
                    </Link>
                </>
            )}
        </>
    );
};

export const ProfileFlashMessages: FC<Props> = ({ flash }) => {
    const messages = [
        {
            value: flash?.notice,
            variant: 'success' as const,
            icon: CircleCheck,
        },
        {
            value: flash?.alert,
            variant: 'destructive' as const,
            icon: CircleAlert,
        },
        {
            value: flash?.warning,
            variant: 'warning' as const,
            icon: TriangleAlert,
        },
    ].filter((message) => message.value != null);

    if (messages.length === 0) return null;

    return (
        <div className="fixed top-5 right-0 left-0 z-[100] mx-auto w-full max-w-3xl space-y-2 px-4">
            {messages.map(({ value, variant, icon: Icon }) => (
                <Alert key={variant} variant={variant}>
                    <Icon />
                    <AlertTitle className="line-clamp-none leading-relaxed">
                        {content(value!)}
                    </AlertTitle>
                </Alert>
            ))}
        </div>
    );
};
